import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Minus,
  Plus,
  Trash2,
  Tag,
  ShoppingBag,
  Truck,
  ShieldCheck,
  Lock,
  Pencil,
  CheckCircle2,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatPrice } from "@/config/pricing";

/** Jeton CSRF Rails injecté dans le <head> */
function csrfToken() {
  return typeof document !== "undefined"
    ? document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    : "";
}

function CsrfInput() {
  return <input type="hidden" name="authenticity_token" value={csrfToken()} />;
}

interface CartAddress {
  firstname: string;
  lastname: string;
  street: string;
  street2: string;
  city: string;
  zip: string;
  country: string;
  tel: string;
  delivery_country_id?: string;
}

interface DeliveryCountry {
  id: string | number;
  name: string;
  delivery_price: number;
}

interface CartProduct {
  id: number | string;
  name: string;
  social_network_id?: number | string | null;
  yearbook_id?: number | string | null;
  product_id?: number | string | null;
  yearbook_format?: string | null;
  item?: string | null;
  item_type?: string | null;
  preview?: string | null;
  price: number;
  quantity?: number;
}

interface CartPrices {
  price_delivery: number;
  price_delivery_promo: number;
  price_final: number;
  price_products: number;
  price_products_promo: number;
}

interface CartActionResponse {
  order_multiple?: Partial<CartPrices>;
}

interface CartData {
  order_multiple_id?: number | string | null;
  user_id?: number | string | null;
  /** Multiplicateur de points fidélité (ex. 2 pour des points doublés) */
  coef_points?: number;
  address_id: number | string | null;
  address: CartAddress | null;
  products: CartProduct[];
  delivery_countries: DeliveryCountry[];
  promo_code?: string | null;
  promo_discount?: number;
  /** Codes promo disponibles pour l'utilisateur : [{ code, amount }] */
  codes?: { code: string; amount: string }[];
  prices?: CartPrices | null;
}

const emptyAddress: CartAddress = {
  firstname: "",
  lastname: "",
  street: "",
  street2: "",
  city: "",
  zip: "",
  country: "",
  tel: "",
  delivery_country_id: "",
};

const defaultCart: CartData = {
  order_multiple_id: null,
  address_id: null,
  address: null,
  products: [],
  delivery_countries: [
    { id: 1, name: "France métropolitaine", delivery_price: 4.9 },
    { id: 2, name: "Belgique", delivery_price: 7.9 },
    { id: 3, name: "Suisse", delivery_price: 12.9 },
  ],
  promo_code: null,
  promo_discount: 0,
  prices: null,
};

export default function Cart() {
  const [cart, setCart] = useState<CartData>(defaultCart);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [addressOpen, setAddressOpen] = useState(false);
  const [form, setForm] = useState<CartAddress>(emptyAddress);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [promoInput, setPromoInput] = useState("");
  const [promoError, setPromoError] = useState<string | null>(null);
  const [codesOpen, setCodesOpen] = useState(false);
  const [showStreet2, setShowStreet2] = useState(false);
  const [applyingCode, setApplyingCode] = useState<string | null>(null);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const priceRequestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetch("/carts.json")
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data: CartData = { ...defaultCart, ...json };
        // order_multiple_id provient du load général : on accepte soit le champ
        // explicite "order_multiple_id", soit le champ "id" à la racine.
        if (data.order_multiple_id == null && json?.id != null) {
          data.order_multiple_id = json.id;
        }
        setCart(data);
        setQuantities(
          Object.fromEntries((data.products || []).map((p) => [String(p.id), p.quantity ?? 1]))
        );
        if (data.address) {
          setForm({ ...emptyAddress, ...data.address });
          setShowStreet2(Boolean(data.address.street2));
        } else {
          // Pays d'expédition : le 1er pays disponible est présélectionné
          setForm({
            ...emptyAddress,
            delivery_country_id:
              data.delivery_countries?.[0] != null
                ? String(data.delivery_countries[0].id)
                : "",
          });
          setShowStreet2(false);
          setAddressOpen(true);
        }
        if (data.promo_code) setPromoInput(data.promo_code);
      })
      .catch(() => {
        if (!cancelled) setAddressOpen(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const products = cart.products || [];
  const countries = cart.delivery_countries || [];

  const selectedCountry = useMemo(
    () => countries.find((c) => String(c.id) === String(cart.address?.delivery_country_id)),
    [countries, cart.address]
  );

  const subtotal = useMemo(
    () => products.reduce((sum, p) => sum + p.price * (quantities[String(p.id)] ?? 1), 0),
    [products, quantities]
  );
  const deliveryPrice = cart.address ? selectedCountry?.delivery_price ?? 0 : 0;
  const discount = cart.promo_discount ?? 0;
  const total = Math.max(0, subtotal + deliveryPrice - discount);

  // Prix fournis par le backend dans le load (prioritaires sur le calcul local)
  const p = cart.prices ?? null;
  const subTotalDisplay = p?.price_products ?? subtotal;
  const subTotalPromo = p?.price_products_promo ?? 0;
  const subTotalFinal = Math.max(0, subTotalDisplay - subTotalPromo);

  const deliveryDisplay = p?.price_delivery ?? deliveryPrice;
  const deliveryPromo = p?.price_delivery_promo ?? 0;
  const deliveryFinal = Math.max(0, deliveryDisplay - deliveryPromo);

  const totalDisplay = p?.price_final ?? total;
  const hasAnyPromo = subTotalPromo > 0 || deliveryPromo > 0;
  // 1 point fidélité par euro dépensé, multiplié par coef_points du load (ex. 2 pour les points doublés)
  const pointsEarned = Math.max(0, Math.floor(subTotalFinal * (cart.coef_points ?? 1)));

  const updatePricesFromResponse = (json: CartActionResponse | undefined, requestId: number) => {
    if (requestId !== priceRequestId.current || !json?.order_multiple) return;

    const nextPrices = json.order_multiple;
    setCart((current) => ({
      ...current,
      prices: {
        price_products: Number(nextPrices.price_products ?? current.prices?.price_products ?? 0),
        price_delivery: Number(nextPrices.price_delivery ?? current.prices?.price_delivery ?? 0),
        price_final: Number(nextPrices.price_final ?? current.prices?.price_final ?? 0),
        price_delivery_promo: Number(
          nextPrices.price_delivery_promo ?? current.prices?.price_delivery_promo ?? 0
        ),
        price_products_promo: Number(
          nextPrices.price_products_promo ?? current.prices?.price_products_promo ?? 0
        ),
      },
    }));
  };

  const postJson = (url: string, payload: Record<string, unknown>) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(payload),
    }).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.json() as Promise<CartActionResponse>;
    });

  const updateQuantity = (id: string | number, next: number) => {
    const qty = Math.max(1, Math.min(99, next));
    const requestId = ++priceRequestId.current;
    setQuantities((q) => ({ ...q, [String(id)]: qty }));
    postJson("/carts/update_quantity_product", {
      order_multiple_product_id: id,
      quantity: qty,
    })
      .then((json) => updatePricesFromResponse(json, requestId))
      .catch(() => undefined);
  };

  const removeProduct = (id: string | number) => {
    const requestId = ++priceRequestId.current;
    setCart((c) => ({ ...c, products: c.products.filter((p) => String(p.id) !== String(id)) }));
    postJson("/carts/delete_order_product", {
      order_multiple_id: cart.order_multiple_id,
      order_multiple_product_id: id,
    })
      .then((json) => updatePricesFromResponse(json, requestId))
      .catch(() => undefined);
  };

  const applyPromo = (codeArg?: string) => {
    const code = (codeArg ?? promoInput).trim();
    if (!code) return;
    setPromoError(null);
    setApplyingCode(code);
    const requestId = ++priceRequestId.current;
    postJson("/carts/send_code_promo", {
      order_multiple_id: cart.order_multiple_id,
      code_promo: code,
    })
      .then((json) => {
        updatePricesFromResponse(json, requestId);
        setCart((c) => ({ ...c, promo_code: code, promo_discount: 0 }));
        setPromoInput(code);
        setCodesOpen(false);
      })
      .catch(() =>
        setPromoError("Impossible de vérifier ce code pour le moment.")
      )
      .finally(() => setApplyingCode(null));
  };

  const openAddressDialog = () => {
    setForm(cart.address ? { ...emptyAddress, ...cart.address } : emptyAddress);
    setShowStreet2(Boolean(cart.address?.street2));
    setErrors({});
    setAddressOpen(true);
  };

  const requiredFields: (keyof CartAddress)[] = [
    "firstname",
    "lastname",
    "street",
    "city",
    "zip",
    "country",
    "tel",
    "delivery_country_id",
  ];

  const submitAddress = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors: Record<string, boolean> = {};
    requiredFields.forEach((f) => {
      if (!String(form[f] ?? "").trim()) nextErrors[f] = true;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const country = countries.find((c) => String(c.id) === String(form.delivery_country_id));
    const payload = {
      order_multiple_id: cart.order_multiple_id != null ? String(cart.order_multiple_id) : undefined,
      address: {
        id: cart.address_id != null ? String(cart.address_id) : undefined,
        name: `${form.firstname} ${form.lastname}`.trim(),
        firstname: form.firstname,
        lastname: form.lastname,
        street: form.street,
        street2: form.street2,
        zip: form.zip,
        city: form.city,
        country: form.country,
        tel: form.tel,
      },
      price_delivery: country ? String(country.delivery_price) : undefined,
      country_delivery_id: form.delivery_country_id ? String(form.delivery_country_id) : undefined,
    };

    const requestId = ++priceRequestId.current;
    fetch("/carts/save_address", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json() as Promise<CartActionResponse>;
      })
      .then((json) => {
        setCart((c) => ({ ...c, address: { ...form } }));
        setAddressOpen(false);
        updatePricesFromResponse(json, requestId);
      })
      .catch(() => {
        setCart((c) => ({ ...c, address: { ...form } }));
        setAddressOpen(false);
      });
  };

  const setField = (key: keyof CartAddress, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((err) => ({ ...err, [key]: false }));
  };

  const fieldClass = (key: keyof CartAddress) =>
    errors[key] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10">
      <Navigation />

      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-2">
            <span className="hidden sm:block h-px w-12 bg-gradient-to-r from-transparent to-primary/60" />
            <h1 className="text-4xl md:text-5xl font-bold leading-none bg-gradient-to-r from-[#f7bbac] to-[#f9bb72] bg-clip-text text-transparent">
              Mon panier
            </h1>
            <span className="hidden sm:block h-px w-12 bg-gradient-to-l from-transparent to-primary/60" />
          </div>
          <p className="text-center text-muted-foreground mb-10">
            Vérifiez vos albums, votre adresse de livraison, puis finalisez votre commande.
          </p>

          {loading ? (
            <p className="text-center text-muted-foreground py-20">Chargement de votre panier…</p>
          ) : (
            <div className="grid lg:grid-cols-[3fr_2fr] gap-8 items-start">
              {/* Colonne gauche */}
              <div className="space-y-6">
                {/* Points fidélité */}
                {pointsEarned > 0 && (
                  <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--stats-orange)/0.4)] bg-[hsl(var(--stats-orange)/0.12)] px-4 py-3">
                    <Star className="w-4 h-4 shrink-0 fill-current text-[hsl(var(--earth-brown))]" />
                    <p className="text-sm text-[hsl(var(--earth-brown))]">
                      Cette commande vous rapporte{" "}
                      <span className="font-semibold">+{pointsEarned} points</span> à transformer en
                      bons d&apos;achat.
                    </p>
                  </div>
                )}
                {/* Produits */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ShoppingBag className="w-5 h-5 text-primary" />
                      Vos articles
                      <span className="text-sm font-normal text-muted-foreground">
                        ({products.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {products.length === 0 ? (
                      <div className="text-center py-10">
                        <p className="text-muted-foreground mb-4">Votre panier est vide.</p>
                        <Button variant="rainbow" asChild>
                          <Link to="/social_networks">Choisir un album</Link>
                        </Button>
                      </div>
                    ) : (
                      products.map((p) => {
                        const qty = quantities[String(p.id)] ?? 1;
                        return (
                          <div
                            key={p.id}
                            className="flex gap-4 rounded-xl border border-border/60 p-3 hover:shadow-sm transition-smooth"
                          >
                            <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-muted">
                              {p.preview ? (
                                <img
                                  src={p.preview}
                                  alt={p.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <ShoppingBag className="w-6 h-6" />
                                </div>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{p.name}</p>
                              {p.yearbook_format && (
                                <p className="text-sm text-muted-foreground">
                                  {p.yearbook_format}
                                </p>
                              )}

                              <div className="flex items-center justify-between mt-3 gap-3">
                                <div className="flex items-center rounded-lg border border-border">
                                  <button
                                    type="button"
                                    aria-label="Diminuer la quantité"
                                    className="px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                                    disabled={qty <= 1}
                                    onClick={() => updateQuantity(p.id, qty - 1)}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="w-8 text-center text-sm font-medium">{qty}</span>
                                  <button
                                    type="button"
                                    aria-label="Augmenter la quantité"
                                    className="px-2 py-1 text-muted-foreground hover:text-foreground"
                                    onClick={() => updateQuantity(p.id, qty + 1)}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>

                                <div className="flex items-center gap-3">
                                  <span className="font-semibold">
                                    {formatPrice(p.price * qty)}
                                  </span>
                                  <button
                                    type="button"
                                    aria-label="Retirer du panier"
                                    className="text-muted-foreground hover:text-destructive transition-smooth"
                                    onClick={() => removeProduct(p.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
                {/* Adresse */}
                <Card className="border-border/60 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MapPin className="w-5 h-5 text-primary" />
                      Adresse de livraison
                    </CardTitle>
                    {cart.address && (
                      <Button variant="ghost" size="sm" onClick={openAddressDialog}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {cart.address ? (
                      <div className="rounded-xl bg-muted/50 p-4 text-sm leading-relaxed">
                        <p className="font-semibold text-foreground">
                          {cart.address.firstname} {cart.address.lastname}
                        </p>
                        <p className="text-muted-foreground">{cart.address.street}</p>
                        {cart.address.street2 && (
                          <p className="text-muted-foreground">{cart.address.street2}</p>
                        )}
                        <p className="text-muted-foreground">
                          {cart.address.zip} {cart.address.city}, {cart.address.country}
                        </p>
                        <p className="text-muted-foreground">{cart.address.tel}</p>
                        {selectedCountry && (
                          <Badge variant="secondary" className="mt-3">
                            <Truck className="w-3 h-3 mr-1" />
                            Expédition : {selectedCountry.name}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-border p-6 text-center">
                        <p className="text-muted-foreground mb-4">
                          Aucune adresse de livraison enregistrée.
                        </p>
                        <Button variant="rainbow" onClick={openAddressDialog}>
                          Ajouter une adresse
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Réassurance */}
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    { icon: Truck, label: "Livraison suivie" },
                    { icon: ShieldCheck, label: "Impression garantie" },
                    { icon: Lock, label: "Paiement sécurisé" },
                  ].map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2 rounded-xl bg-muted/50 px-4 py-3 text-sm text-muted-foreground"
                    >
                      <Icon className="w-4 h-4 text-primary" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Colonne droite : récapitulatif */}
              <Card className="border-border/60 shadow-elegant lg:sticky lg:top-32">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Récapitulatif</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Code promo */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <Tag className="w-4 h-4 text-primary" />
                      Code promo
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={promoInput}
                        onChange={(e) => {
                          setPromoInput(e.target.value);
                          setPromoError(null);
                        }}
                        placeholder="Ex : SOUVENIRS10"
                        className="uppercase"
                      />
                      <Button variant="outline" onClick={() => applyPromo()} disabled={!promoInput.trim()}>
                        Appliquer
                      </Button>
                    </div>
                    {(cart.codes?.length ?? 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => setCodesOpen(true)}
                        className="text-xs text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        Voir mes codes dispo ({cart.codes!.length})
                      </button>
                    )}
                    {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                    {cart.promo_code && !promoError && hasAnyPromo && (
                      <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Code {cart.promo_code} appliqué
                      </p>
                    )}
                  </div>

                  {/* Codes promo disponibles */}
                  <Dialog open={codesOpen} onOpenChange={setCodesOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Mes codes promo</DialogTitle>
                        <DialogDescription>
                          Cliquez sur « Appliquer » pour utiliser un code sur cette commande.
                        </DialogDescription>
                      </DialogHeader>
                      <ul className="space-y-2">
                        {(cart.codes || []).map((c) => (
                          <li
                            key={c.code}
                            className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold uppercase truncate">{c.code}</p>
                              {c.amount && (
                                <p className="text-xs text-muted-foreground">{c.amount}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => applyPromo(c.code)}
                              disabled={applyingCode != null}
                            >
                              {applyingCode === c.code ? "..." : "Appliquer"}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </DialogContent>
                  </Dialog>


                  <div className="space-y-2 text-sm border-t border-border/60 pt-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sous-total</span>
                      {subTotalPromo > 0 ? (
                        <span className="line-through text-muted-foreground">{formatPrice(subTotalDisplay)}</span>
                      ) : (
                        <span>{formatPrice(subTotalDisplay)}</span>
                      )}
                    </div>
                    {subTotalPromo > 0 && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-primary">
                          <span>Réduction produits</span>
                          <span>-{formatPrice(subTotalPromo)}</span>
                        </div>
                        <div className="flex justify-end">
                          <span className="text-lg font-semibold text-primary">
                            {subTotalFinal === 0 ? "Offert" : formatPrice(subTotalFinal)}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-border/40 pt-3 mt-3">
                      <span className="text-muted-foreground">Frais de livraison</span>
                      {cart.address ? (
                        deliveryPromo > 0 ? (
                          <span className="line-through text-muted-foreground">{formatPrice(deliveryDisplay)}</span>
                        ) : (
                          <span>{formatPrice(deliveryDisplay)}</span>
                        )
                      ) : (
                        <span>À définir</span>
                      )}
                    </div>
                    {deliveryPromo > 0 && cart.address && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-primary">
                          <span>Réduction livraison</span>
                          <span>-{formatPrice(deliveryPromo)}</span>
                        </div>
                        <div className="flex justify-end">
                          <span className="text-lg font-semibold text-primary">
                            {deliveryFinal === 0 ? "Offert" : formatPrice(deliveryFinal)}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline border-t border-border/60 pt-3 mt-3">
                      <span className="font-semibold">Total</span>
                      <span className="text-2xl font-bold text-primary">{formatPrice(totalDisplay)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">TVA incluse</p>
                    {pointsEarned > 0 && (
                      <div className="flex items-center justify-between gap-2 rounded-xl border border-[hsl(var(--stats-orange)/0.4)] bg-[hsl(var(--stats-orange)/0.12)] px-3 py-2.5">
                        <span className="flex items-center gap-2 text-sm font-medium text-[hsl(var(--earth-brown))]">
                          <Star className="w-4 h-4 shrink-0 fill-current" />
                          Points fidélité à gagner
                        </span>
                        <span className="text-sm font-bold text-[hsl(var(--earth-brown))] whitespace-nowrap">
                          +{pointsEarned} pts
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-3 rounded-xl bg-muted/50 p-3">
                    <Checkbox
                      id="cgv"
                      checked={cgvAccepted}
                      onCheckedChange={(v) => setCgvAccepted(v === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="cgv" className="text-sm font-normal leading-snug cursor-pointer">
                      J'accepte les{" "}
                      <Link to="/cgv" className="text-primary underline underline-offset-2">
                        Conditions générales de vente
                      </Link>
                    </Label>
                  </div>

                  <form action="/carts/paid" method="POST">
                    <CsrfInput />
                    <input
                      type="hidden"
                      name="user_id"
                      value={cart.user_id != null ? String(cart.user_id) : ""}
                    />
                    <input
                      type="hidden"
                      name="order_multiple_id"
                      value={cart.order_multiple_id != null ? String(cart.order_multiple_id) : ""}
                    />
                    <Button
                      type="submit"
                      variant="rainbow"
                      size="lg"
                      className="w-full"
                      disabled={!cgvAccepted || products.length === 0 || !cart.address}
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Valider et payer
                    </Button>
                  </form>

                  {!cart.address && (
                    <p className="text-xs text-center text-muted-foreground">
                      Ajoutez une adresse de livraison pour continuer.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Dialog adresse */}
      <Dialog
        open={addressOpen}
        onOpenChange={(open) => {
          // obligatoire tant qu'aucune adresse n'existe
          if (!open && !cart.address) return;
          setAddressOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {cart.address ? "Modifier l'adresse de livraison" : "Adresse de livraison"}
            </DialogTitle>
            <DialogDescription>
              Tous les champs sont obligatoires pour expédier votre album.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitAddress} className="space-y-4">

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstname">Prénom</Label>
                <Input
                  id="firstname"
                  name="address[firstname]"
                  value={form.firstname}
                  onChange={(e) => setField("firstname", e.target.value)}
                  className={fieldClass("firstname")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastname">Nom</Label>
                <Input
                  id="lastname"
                  name="address[lastname]"
                  value={form.lastname}
                  onChange={(e) => setField("lastname", e.target.value)}
                  className={fieldClass("lastname")}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="street">Adresse</Label>
              <Input
                id="street"
                name="address[street]"
                value={form.street}
                onChange={(e) => setField("street", e.target.value)}
                className={fieldClass("street")}
              />
            </div>

            {showStreet2 ? (
              <div className="space-y-1.5">
                <Label htmlFor="street2">Complément d'adresse</Label>
                <Input
                  id="street2"
                  name="address[street2]"
                  value={form.street2}
                  onChange={(e) => setField("street2", e.target.value)}
                  autoFocus
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowStreet2(true)}
                className="text-xs text-primary underline underline-offset-2 hover:opacity-80"
              >
                Ajouter un complément
              </button>
            )}

            <div className="grid grid-cols-[1fr_2fr] gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="zip">Code postal</Label>
                <Input
                  id="zip"
                  name="address[zip]"
                  value={form.zip}
                  onChange={(e) => setField("zip", e.target.value)}
                  className={fieldClass("zip")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  name="address[city]"
                  value={form.city}
                  onChange={(e) => setField("city", e.target.value)}
                  className={fieldClass("city")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="country">Pays</Label>
                <Input
                  id="country"
                  name="address[country]"
                  value={form.country}
                  onChange={(e) => setField("country", e.target.value)}
                  className={fieldClass("country")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tel">Téléphone</Label>
                <Input
                  id="tel"
                  name="address[tel]"
                  value={form.tel}
                  onChange={(e) => setField("tel", e.target.value)}
                  className={fieldClass("tel")}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Pays d'expédition</Label>
              <Select
                value={form.delivery_country_id ? String(form.delivery_country_id) : undefined}
                onValueChange={(v) => setField("delivery_country_id", v)}
              >
                <SelectTrigger className={fieldClass("delivery_country_id")}>
                  <SelectValue placeholder="Choisir un pays d'expédition" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  {countries.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} — {formatPrice(c.delivery_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                type="hidden"
                name="address[delivery_country_id]"
                value={form.delivery_country_id ?? ""}
              />
            </div>

            {Object.keys(errors).some((k) => errors[k]) && (
              <p className="text-sm text-destructive">Merci de remplir tous les champs.</p>
            )}

            <DialogFooter className="gap-2">
              {cart.address ? (
                <Button type="button" variant="ghost" onClick={() => setAddressOpen(false)}>
                  Annuler
                </Button>
              ) : (
                <Button type="button" variant="ghost" asChild>
                  <Link to="/social_networks">Fermer</Link>
                </Button>
              )}
              <Button type="submit" variant="rainbow">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}